// The Platform lists: organisations, environments, workspaces, legacy workflows and runs.
import type { ReactNode } from "react";

import { plural, rich } from "../../../helpers";
import type { RunFilter } from "../../../../workloads/runs";

const p = plural("en");

export default {
  orgs: {
    title: "Organisations",
    description: "Tenants. Each is one Incus project with its own quota; nothing is shared between them.",
    emptyTitle: "No organisations",
    emptyBody: "A platform admin creates the first organisation; it becomes an Incus project with the quota you set.",
    environments: (n: number) => p(n, { one: "environment", other: "environments" }),
    workflows: (n: number) => p(n, { one: "workflow", other: "workflows" }),
    recentRuns: (n: number) => p(n, { one: "recent run", other: "recent runs" }),
    cpuQuota: "CPU quota",
    memoryQuota: "Memory quota",
    quotaNote: (disk: number) => `Used is what its environments' limits reserve; disk quota ${disk} GiB.`,
    since: (day: string) => `Since ${day}`,
    spend: (amount: ReactNode) => rich(" · ", amount, " in 30 days"),
    environmentsLink: "Environments",
  },
  cores: "cores",
  environments: {
    title: "Environments",
    description: "Isolated Incus instances, each with its own network namespace, VPN, Docker daemon, secrets and limits.",
    emptyTitle: "No environments",
    emptyBody: "An environment is an Incus instance with its own network namespace, VPN, Docker daemon, secrets and limits.",
    columns: { environment: "Environment", status: "Status", cpu: "CPU", memory: "Memory", network: "Image and network", runs: "Runs" },
    live: (n: number) => `${n} live`,
    noVpn: "No VPN",
    vpn: "VPN",
    vpnOf: (type: string, required: boolean) => `${type} VPN${required ? ", required" : ""}`,
    egressDomains: (n: number) => p(n, { one: "# egress domain", other: "# egress domains" }),
  },
  workspaces: {
    title: "Workspaces",
    description: "Repositories on a persistent volume inside an environment, and the long-lived worker you open a terminal into.",
    emptyTitle: "No workspaces",
    emptyBody: "A workspace holds repositories on a persistent volume inside an environment, and the long-lived worker you SSH into.",
    environmentLink: "Environment",
  },
  workspace: {
    openTerminal: "Open terminal",
    noRepos: "No repositories yet.",
    git: "Git",
    writable: "Writable",
    readOnly: "Read-only",
    dependencies: "Dependencies",
    persisted: "Persisted",
    freshEachRun: "Fresh each run",
    interactiveWorker: "Interactive worker",
    running: "Running",
    none: "None",
    volume: "Volume",
  },
  legacy: {
    title: "Legacy workflows",
    description:
      "The organisation model's single-prompt workflows: a prompt, a trigger, a guard and a tool policy. Each run gets a fresh worker and a capability token naming exactly what it may call.",
    emptyTitle: "No workflows",
    emptyBody: "A workflow is a prompt plus a trigger (cron, interval, ticket filter or manual), a guard script and a tool policy.",
    runs30d: "runs, 30 d",
    succeeded: "succeeded",
    avgCost: "avg cost",
    noRunsYet: "No runs yet",
    lastRun: (when: string) => `Last run ${when}`,
    runNow: "Run now",
  },
  runs: {
    title: "Runs",
    description: "What the agents did and are doing. A run's worker is destroyed when it ends; its results and encrypted conversation stay.",
    filterLabel: "Filter by status",
    filters: {
      all: "All",
      waiting: "Waiting for you",
      running: "Running",
      succeeded: "Succeeded",
      failed: "Failed",
    } satisfies Record<RunFilter, string>,
    emptyNone: "No runs",
    emptyFiltered: "No runs with this status",
    emptyBody: "Runs appear when a workflow fires or you start one. Live events arrive over the relay.",
    columns: { run: "Run", status: "Status", where: "Where", queued: "Queued", duration: "Duration", tokens: "Tokens in / out", cost: "Cost" },
    fallbackName: "Run",
    costNote: "Costs are the agent CLI's own report; subscription runs show the equivalent API price.",
  },
};
